import { Injectable } from '@angular/core';
import { IDatiqUser } from '@dat/datiq-user';
import { Context } from '@opentelemetry/api';
import { ReadableSpan, Span } from '@opentelemetry/sdk-trace-web';
import { SemanticAttributes } from '@opentelemetry/semantic-conventions';
import {
    getLogOtelBrowserVersion,
    ILogOtelSpanProcessorService,
    LogOtelUser,
} from './models';
import { getLogOtelBrowser } from './models';

/**
 * Keeping these here because these are specific to OTEL
 * attributes and when we update these processors, it will
 * make it easier for us to add.
 */

/**
 * To add a new Attribute Key, you can do the following:
 * - Add a new string to the LogOtelAttributeKeysKey type representing
 *   the key to use in the object.
 * - Then add the value to the LogOtelAttributeKeys record.
 */
type LogOtelAttributeKeysKey =
    | 'UserAgent'
    | 'Url'
    | 'Path'
    | 'Host'
    | 'UserCsbId'
    | 'UserId'
    | 'UserName'
    | 'Browser'
    | 'BrowserVersion'
    | 'UserId'
    | 'UserName';

/**
 * The value of the keys should try to use the SemanticAttributes
 * from the @opentelemetry/semantic-conventions package. If we need
 * to do a custome one, it should follow the format:
 *
 * [GROUP].[UNDERSCORED_ATTRIBUTE]]
 */
const LogOtelAttributeKeys: Record<LogOtelAttributeKeysKey, string> = {
    UserAgent: SemanticAttributes.HTTP_USER_AGENT,
    Url: SemanticAttributes.HTTP_URL,
    Host: SemanticAttributes.HTTP_HOST,
    Path: 'http.path',
    Browser: 'http.browser',
    BrowserVersion: 'http.browser_version',
    UserId: 'user.id',
    UserName: 'user.username',
    UserCsbId: 'user.csb_id',
};

/**
 * These are the capabilities that we want to track to help us
 * debug various errors and effects on our app.
 *
 * ! We don't have this information during the documentload
 * ! instrumentation. The user will need to be logged in for
 * ! this to work.
 */
export const DatiqLogCapabilities: string[] = [
    'cp_enable_rate_lookup_api',
    'cp_enable_iq_rates_ui',
    'cp_enable_tru_rate_api',
    'cp_enable_tru_rate_api',
    'cp_rfp_tool_enabled',
];

export const LogOtelDefaultAttributes = {
    [LogOtelAttributeKeys.UserAgent]: navigator.userAgent,
    [LogOtelAttributeKeys.Url]: window.location.href,
    [LogOtelAttributeKeys.Path]: window.location.pathname,
    [LogOtelAttributeKeys.Host]: window.location.host,
    [LogOtelAttributeKeys.Browser]: getLogOtelBrowser(navigator.userAgent),
    [LogOtelAttributeKeys.BrowserVersion]: getLogOtelBrowserVersion(
        navigator.userAgent,
    ),
};
@Injectable({
    providedIn: 'root',
})
export class LogOtelSpanProcessorService
    implements ILogOtelSpanProcessorService
{
    _attributes: Record<string, string | boolean | number> = {};

    constructor() {}

    setDefaultAttributes(
        attributes: Record<string, string | boolean | number> = {},
    ) {
        this._attributes = {
            ...LogOtelDefaultAttributes,
            ...attributes,
        };
    }

    forceFlush(): Promise<void> {
        return Promise.resolve();
    }
    onStart(span: Span, parentContext: Context): void {
        Object.entries(this._attributes).forEach(([key, value]) =>
            span.setAttribute(key, value),
        );
    }

    // Has to be implemented, but we don't need it at this time.
    onEnd(span: ReadableSpan): void {}

    shutdown(): Promise<void> {
        return Promise.resolve();
    }

    setUser(
        user: LogOtelUser,
        capabilities: string[] = DatiqLogCapabilities,
    ): void {
        /**
         * The reason why we need iterate over the list instead of the object is
         * because for Observe querying purposes it would be hard to say "if the user.[capability] doesn't exists"
         * It is much easier to say does `user.cp_enable_rate_lookup_api = true.
         *
         * TODO: Maybe figure out how we can do this with all capabilities
         */
        const capabilitiesAttributes = capabilities.reduce(
            (currentCaps: Record<string, boolean>, capability: string) => ({
                ...currentCaps,
                [this.createUserKey(capability)]:
                    !!user?.capabilities?.[capability],
            }),
            {},
        );

        this.updateAttributes({
            ...capabilitiesAttributes,
            // TODO: Since this could be considered PII, we need to talk with the squad to see if can add this.
            [LogOtelAttributeKeys.UserId]: user?.id ?? '',
            [LogOtelAttributeKeys.UserCsbId]: user?.oAuthProfile?.csbId ?? -1,
        });
    }

    createUserKey(capability: string) {
        return `user.${capability}`;
    }

    updateAttributes(
        attributes: Record<string, string | boolean | number>,
    ): void {
        this._attributes = { ...this._attributes, ...attributes };
    }
}
